/**
 *
 * @alias components.core.SavesPlugin
 * @classdesc Manages game saves, allowing for multiple player saves
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.saves
 * </pre>
 *
 * The default storage ID and version number can be overridden in the [getConfig()]{@link getConfigBase} component options
 * <pre>
 * components : {
 *  saves : { data :  { storageID : 'storageid', versionNumber : 0 } }
 * }
 * </pre>
 *
 * Or by calling the [setStorageID]{@link components.core.SavesPlugin#setStorageID} and [setVersionNumber]{@link components.core.SavesPlugin#setVersionNumber} methods
 *
 * <pre>
 * this.game.appUrl.storageID( storageID )
 * this.game.appUrl.setVersionNumber( version )
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', SavesPlugin, true, 'scenekey' || null,  { storageID : 'storageid' } );
 * </pre>
 *
 */
class SavesPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._saves = {};
    this._saveID = 'newsave';
    this._storageId = 'saves';
    this._versionNumber = 0;
  }

  init(config) {
    if (config) {
      this.setStorageId(config.storageID || this._storageId);
      this._versionNumber = config.versionNumber || this._versionNumber;
    }
  }

  onGameReady() {
    this.loadData();
  }

  /**
   * Check if save is corrupt or not
   * @param {string} [id=active save id] the id of the slot to delete
   */
  isSaveCorrupt(id) {
    const saveID = id || this._saveID;
    if (this.hasSave(saveID)) {
      return this._saves[saveID].version != this._versionNumber;
    }
    return false;
  }

  /**
   * Mostly used internaly and done automatically.
   *
   * Checks all current saves against the current version number.
   *
   * Triggers a prompt to remove any corrupt saves from storage.
   */
  checkCorruptedSaves(triggerAlertOption) {
    const saves = this.getSaves();
    const corrupt = saves.filter((save) => save.version != this._versionNumber);
    if (triggerAlertOption && corrupt.length > 0) {
      const msg =
        'Some game saves are incompatible due to development changes.\n\nClick OK to delete corrupted saves';
      if (window.confirm(msg)) {
        corrupt.forEach((entry) => {
          this.deleteSave(entry.id);
        });
      }
    }
    return corrupt;
  }

  /**
   * The key used when saving or retrieving from the storage system
   * @type {string}
   * @default 'saves'
   */
  get storageId() {
    return this._storageId;
  }

  set storageId(storageId) {
    this.setStorageId(storageId);
  }

  /**
   * Set the key used when saving or retrieving from the storage system
   * @param {string} storageID The key to use
   */
  setStorageId(storageId) {
    this._storageId = storageId || SAVES_ID;
    this.loadData();
  }

  /**
   * The current save model version number to use when saving.
   * WIll run version control when set.
   * @type {string}
   * @default 0
   */
  get versionNumber() {
    return this._versionNumber;
  }

  set versionNumber(versionNumber) {
    this.setVersionNumber(versionNumber);
  }

  /**
   * Set the version number of the current save model.
   * The version number is used to check for corrupt saves should there be any breaking model changes during development.
   * @param {string|number} version an id to act as current save model version
   * @param {boolean} [checkSaves] should run version control check on existing saves after updating version number
   * @param {boolean} [triggerAlertOption] should trigger alert if corrupt saves found
   */
  setVersionNumber(version, checkSaves, triggerAlertOption = false) {
    this._versionNumber = version || this._versionNumber;
    if (checkSaves) return this.checkCorruptedSaves(triggerAlertOption);
  }

  /**
   * The key used when saving or retrieving from the storage system.<br>
   * If no save id has been set when fetching, it will be generated
   * @type {string}
   * @default {string} 'newsave'
   */
  get saveID() {
    return this._saveID;
  }

  set saveID(saveID) {
    this.setSaveId(saveID);
  }

  /**
   * Set the active save ID, if no ID is supplied the current timestamp will be used.<br>
   * Returns the save ID
   * @param {string} [saveID=current timestamp] The id to use
   * @returns {string} the save id
   */
  setSaveId(saveID) {
    this._saveID = saveID || this._saveID;
    this._create({ saveID });
    return this._saveID;
  }

  /**
   * Get the active save id
   * @returns {string|number}
   */
  getSaveId() {
    return this._saveID;
  }

  /**
   * Mostly used internally, loads the saved data from storage
   */
  loadData() {
    this._saves = this.loadFromStorage() || {};
  }

  /*
  ===================================================================================
    CREATE AND DELETE
  ===================================================================================
  */

  /**
   *
   * Save a data model to the current or specified save slot
   *
   * @param {Object} object
   * @param {Object} object.data the data model to save
   * @param {string} [object.id=active slot id] the id of the slot to save too
   */
  save({ id, data }) {
    const saveID = id || this._saveID;
    if (saveID) {
      const existing = this._saves[saveID] || this._create({ saveID });
      const merged = { ...existing, ...data };
      merged.version = this._versionNumber;
      this._saves[saveID] = merged;
      this._saveToStorage();
    }
  }

  /**
   * Delete all save slots
   */
  deleteSaves() {
    this._saves = {};
    this.deleteFromStorage();
  }

  /**
   * Delete the current or specified save slot
   * @param {string} [id=active save id] the id of the slot to delete
   */
  deleteSave(id) {
    const saveID = id || this._saveID;
    if (this.hasSave(saveID)) {
      delete this._saves[saveID];
      this._saveToStorage();
    }
  }

  /**
   * Get the saved data for a specific or the current save id
   * @param {string|number} [saveid=active save id] the save id you want the data for
   * @returns {Object}
   */
  getSave(saveid) {
    saveid = saveid || this._saveID;
    return this._saves[saveid] || false;
  }

  /**
   * Check if a slot id has a save
   * @param {*} slotID the id of the slot to check
   * @returns {bool}
   */
  hasSave(slotID) {
    if (this._saves) {
      return this._saves[slotID] || false;
    }
    return false;
  }

  /**
   * CHeck if there are any saves stored
   * @returns {bool}
   */
  playerHasSaves() {
    if (this._saves) {
      return Object.keys(this._saves).length > 0;
    }
    return false;
  }

  /**
   * Get all the saves currently in storage
   * @returns Object[]
   */
  getSaves() {
    const result = Object.keys(this._saves).map((key) => {
      return this._saves[key];
    });
    return result;
  }

  /**
   * Get the number of saves currently in storage
   * @returns {number}
   */
  getSavesCount() {
    if (this._saves) {
      return Object.keys(this._saves).length;
    } else {
      return 0;
    }
  }

  /*
  ====================================================================================================
  DATA SERIALIZATION
  ====================================================================================================
  */

  /**
   * Deserialize a game model from the active, or a specified saved slot
   *
   * @param {Object} object The Model to deserialize into
   * @param {string} modelID The id of the model to deserialize
   * @param {string} [slotID=active slot id] The slot id to use
   */
  deserialize(object, modelID, slotID) {
    slotID = slotID || this._saveID;
    const data = this._saves[slotID];
    const dataSlice = (data && data[modelID]) || null;
    if (dataSlice) {
      const keys = Object.keys(dataSlice);
      for (const key of keys) {
        if (object[key] !== null || object[key] !== undefined) {
          object[key] = dataSlice[key];
        }
      }
    }
  }

  /**
   * Serialize a game model into compatable save slot data
   *
   * @param {Object} object The Model to serialize
   * @param {string} modelID The id of the model to serialize
   * @param {string[]} keys An array of the model keys to serialize
   */
  serialize(object, modelID, keys) {
    const data = {};
    data[modelID] = keys.reduce((obj, key) => {
      obj[key] = object[key];
      return obj;
    }, {});
    return data;
  }

  // =============================================================================================
  // STORAGE
  // =============================================================================================

  /**
   * Save data to Storage, by default this is the localStorage.<br>
   * You should extend this class and overwrite this method if you require different storage needs
   */
  saveToStorage(data) {
    try {
      window.localStorage.setItem(this._storageId, JSON.stringify(data));
    } catch (e) {}
  }

  /**
   * Get data from Storage, by default this is the localStorage.<br>
   * You should extend this class and overwrite this method if you require different storage needs
   */
  loadFromStorage() {
    try {
      const data = window.localStorage.getItem(this._storageId);
      return JSON.parse(data);
    } catch (e) {}
    return undefined;
  }

  /**
   * Delete data from Storage, by default this is the localStorage.<br>
   * You should extend this class and overwrite this method if you require different storage needs
   */
  deleteFromStorage() {
    try {
      window.localStorage.removeItem(this.key);
    } catch (e) {}
  }

  /**
   * @private
   */
  _create({ saveID }) {
    if (!this.hasSave(saveID)) {
      this._saves[saveID] = { id: saveID, version: this._versionNumber };
      this._saveToStorage();
    }
    return this._saves[saveID];
  }

  /**
   * @private
   */
  _saveToStorage() {
    this.saveToStorage(this._saves);
  }
}

export { SavesPlugin };
